import React, { useRef, useState } from "react";
import { Menu } from "primereact/menu";
import { OverlayPanel } from "primereact/overlaypanel";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { Locale, UIState } from "../types/ui-types";
import styled from "styled-components";
import { UserContext, useUserContext } from "../contexts/userContext";

const LanguageSelectContainer = styled.div`
  cursor: pointer;
  margin: 10px auto;
  font-size: 0.9rem;
  position: absolute;
  top: 12px;
  right: 30px;
  z-index: 1000;
`;

const LanguageOption = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
  padding: 8px 13px;
  width: 100%;
  background-color: var(--sumi-blue);
  color: var(--sumi-text-primary);
  font-size: 0.9rem;
  cursor: pointer;

  &:hover {
    background-color: var(--sumi-darker-blue);
  }
`;

const LinkContainer = styled.div<{
  $isActive: boolean;
  $darkTheme: boolean;
}>`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
  background-color: ${({ $isActive }) =>
    $isActive ? "var(--sumi-blue)" : "transparent"};
  color: #717171;
  padding: 8px 13px;
  border-radius: 5px;
  width: 100%;
  line-height: 2rem;
  height: 36px;

  &:hover {
    background-color: var(--sumi-blue);
    color: #ffffff !important;
    transition:
      background-color 0.4s,
      color 0.4s;
    cursor: pointer;
  }
`;

const LanguageSelect: React.FC = () => {
  const [locale, setLocale, theme] = useUIStore(
    useShallow((state: UIState) => [state.locale, state.setLocale, state.theme])
  );
  const [showLanguageSelect] = useState(true);
  const languagePanelRef = useRef<OverlayPanel>(null);

  const { updateUserLanguage } = useUserContext();

  const languageOptions = [
    { label: "English", value: Locale.EN },
    { label: "Suomi", value: Locale.FI },
    { label: "Svenska", value: Locale.SWE },
  ];

  const getLocaleForLanguage = (locale: Locale) => {
    switch (locale) {
      case Locale.FI:
        return "FI";
      case Locale.EN:
        return "EN";
      case Locale.SWE:
        return "SV";
      default:
        return "FI";
    }
  };

  const handleLanguageChange = (value: Locale) => {
    setLocale(value);
    updateUserLanguage(getLocaleForLanguage(value));
    languagePanelRef.current?.hide();
  };

  const getActiveLanguageLabel = () => {
    switch (locale) {
      case Locale.FI:
        return "Suomi";
      case Locale.EN:
        return "English";
      case Locale.SWE:
        return "Svenska";
      default:
        return "Suomi";
    }
  };

  if (!showLanguageSelect) {
    return null;
  }

  return (
    <>
      <LanguageSelectContainer
        id="nav-link-language"
        onClick={(e) => languagePanelRef.current?.toggle(e)}
        style={{}}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            languagePanelRef.current?.toggle(e);
            setTimeout(() => {
              const el = document.getElementById("language-option-1");
              el?.focus();
            }, 0);
            e.preventDefault();
          }
        }}
      >
        <LinkContainer $isActive={false} $darkTheme={theme === "dark"}>
          {getActiveLanguageLabel()}
          <i
            style={{
              fontSize: "0.7rem",
              marginTop: "1px",
            }}
            className="pi pi-chevron-down"
          />
        </LinkContainer>
      </LanguageSelectContainer>
      <OverlayPanel
        unstyled
        ref={languagePanelRef}
        className="language-overlay-panel"
        style={{
          backgroundColor: "#fff",
          boxShadow: "0 0 10px rgba(0,0,0,0.2)",
          borderRadius: "5px",
          width: "120px",
        }}
      >
        <Menu
          style={{ width: "120px" }}
          model={languageOptions.map((option, index) => ({
            label: option.label,

            template: () => (
              <LanguageOption
                id={`language-option-${index + 1}`}
                key={option.value}
                onClick={() => handleLanguageChange(option.value as Locale)}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleLanguageChange(option.value as Locale);
                    e.preventDefault();
                  }
                  if (e.key === "ArrowDown") {
                    if (index === languageOptions.length - 1) {
                      const first =
                        document.getElementById("language-option-1");
                      first?.focus();
                    } else {
                      const next = document.getElementById(
                        `language-option-${index + 2}`
                      );
                      next?.focus();
                    }
                    e.preventDefault();
                  }

                  if (e.key === "ArrowUp") {
                    if (index === 0) {
                      const last = document.getElementById(
                        `language-option-${languageOptions.length}`
                      );
                      last?.focus();
                    } else {
                      const prev = document.getElementById(
                        `language-option-${index}`
                      );
                      prev?.focus();
                    }
                    e.preventDefault();
                  }
                }}
              >
                {option.label}
              </LanguageOption>
            ),
          }))}
        />
      </OverlayPanel>
    </>
  );
};

export default LanguageSelect;
